import type { IntlFormatters } from 'react-intl'
import type { AssessmentTemplate } from '../types/resourceExplorer'
import { AssessmentLevelNamesType, AssessmentType } from '../types/resourceExplorer'

export enum LabelFormat {
  Basic = 'basic',
  WithPercentage = 'withPercentage',
}

export const getTranslationKeysForLevels = (
  assessmentType: AssessmentType,
  levels: number,
  formatMessage: IntlFormatters['formatMessage'],
  levelNamesType?: AssessmentLevelNamesType,
  format: LabelFormat = LabelFormat.WithPercentage,
): string[] => {
  if (assessmentType === AssessmentType.BaselineAssessment) {
    return Array.from({ length: 5 }).map((_, i) =>
      formatMessage({ id: 'baselineAssessment.totalLevel' }, { value: i + 1 }),
    )
  }

  // If this is changed, also change in AssessmentIntervalTextExtractor.cs - GetIntervalText so the change is also reflected in the backend
  const keyBase = `assessments.${levelNamesType ?? AssessmentLevelNamesType.Intervals}${format === LabelFormat.WithPercentage ? 'WithPercentageSpan' : ''}`

  return Array.from({ length: levels }).map((_, i) => formatMessage({ id: `${keyBase}.${levels}.level${i + 1}` }))
}

export const getFilterOptionsForLevels = (
  assessmentType: AssessmentType,
  levels: number,
  formatMessage: IntlFormatters['formatMessage'],
  levelNamesType?: AssessmentLevelNamesType,
  format: LabelFormat = LabelFormat.WithPercentage,
): { label: string; value: number }[] => {
  return getTranslationKeysForLevels(assessmentType, levels, formatMessage, levelNamesType, format).map(
    (label, index) => ({
      label,
      value: index + 1,
    }),
  )
}

export type LevelNamingTemplate = Pick<AssessmentTemplate, 'assessmentType' | 'levels' | 'levelNamesType'>

/**
 * A level as the assessment names it, e.g. "Robust" or "Poor", matching the level chips on the
 * assessment pages. Baseline and plain level naming read "Level N".
 */
export const formatLevelLabel = (
  template: LevelNamingTemplate | undefined,
  level: number,
  formatMessage: IntlFormatters['formatMessage'],
): string => {
  if (template && template.assessmentType !== AssessmentType.BaselineAssessment) {
    const levelLabels = getTranslationKeysForLevels(
      template.assessmentType,
      template.levels ?? 5,
      formatMessage,
      template.levelNamesType,
      LabelFormat.Basic,
    )
    const label = levelLabels[level - 1]
    if (label) return label
  }
  return formatMessage({ id: 'expectation.level' }, { level })
}
